const DEFAULT_SUFFIX = ' - Blackboard Illuminate';

// More than one mounted component can want to name the page at once - an
// ancestor with a generic title and a descendant with a specific one. They ask
// in tree order, so the deepest one wins, but writing straight to
// document.title means every render pass puts the generic value on the tab for
// an instant before the specific one replaces it, and the browser paints it.
// That is what makes the tab flicker while clicking around, even when the
// title it settles on never actually changes.
//
// So a pass's requests are collected and only the last one is applied, in a
// microtask once the pass is over: same winner, one value for the browser to
// show. Rendering here is synchronous (React 17, legacy render), so a
// microtask reliably lands after the whole pass.
let pendingTitle: string | null = null;
let isFlushScheduled = false;

const flushTitle = () => {
    isFlushScheduled = false;
    const title = pendingTitle;
    pendingTitle = null;
    if (title !== null && document.title !== title) {
        document.title = title;
    }
};

export const setApplicationTitle = (title: string): void => {
    if (typeof document === 'undefined') {
        return;
    }
    pendingTitle = title;
    if (!isFlushScheduled) {
        isFlushScheduled = true;
        Promise.resolve().then(flushTitle);
    }
};

/**
 * Names the browser tab.
 *
 * Pass `null` when the caller is rendered *inside* a page rather than being
 * one - a slide-over panel, say - so that opening it leaves the tab to
 * whichever component actually owns the page.
 */
export const useApplicationTitle = (
    title: string | null,
    suffix = DEFAULT_SUFFIX,
) => {
    if (title !== null) {
        setApplicationTitle(`${title}${suffix}`);
    }
};

export default useApplicationTitle;
